import { PayNowMakePaymentRequest } from "../models/pay-now-make-payment";
import {patientService} from '../../patient/services/patient-service';
import {
  PatientPaymentSchedule
} from '../../patient/components/models/patient-payment.schedule';
import {
  PatientPaymentProgram
} from '../../patient/components/models/patient-payment-program';
import {PatientInfo} from '../components/mobile-payment-view';
import {Payment} from '../../patient/components/models/payment';
import {AxiosResultStatus} from '../../shared/service/axios-result-status';
import { AxiosSavePayload } from "src/shared/service/axios-save-payload";
import { axiosSaveHelper } from "src/shared/service/axios-save-helper";
import { AxiosReadPayload } from "../../shared/service/axios-read-payload";
import { axiosReadHelper } from "../../shared/service/axios-read-helper";
import { PatientEncounter } from "src/patient/components/models/patient-encounter";
import moment from "moment";
import { User } from "src/security/model/user";
import { Utils } from 'src/shared/utils';
import { DetailedLedger } from "src/shared/model/ledger";
import { PatientViewModel } from "src/patient/components/models/patient-view-model";
import { LedgerUtils } from "src/shared/ledger-utils";
import { RootState } from "src/shared/state/root-reducer";

class PayNowService {

  async checkEmail(data: { clientId: number | string; email: string, authToken: string }) {
    const serviceUrl = `/patient-encounter/v2/patient/encounter/keys?clientId=${data.clientId}&isConverted=true&linkedUserId=${encodeURIComponent(data.email)}`;
      return await this.postApi("GET", serviceUrl, null, data.authToken );
  }

  async getPaymentChannels(authToken: string) {
    const serviceurl = `/payment/v2/payment/paymentchannels`;
    return await this.postApi("GET", serviceurl, null, authToken);
  }

  async getPatientMasterLedger(clientId: number, patientId: number): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      dataId: -1,
      url: `payment/v2/payment/ledger?clientId=${clientId}&patientId=${patientId}`

    };
    return await axiosReadHelper(payload);
  }

  async updatePaymentDetails(paramId: number, paymentDetails: any): Promise<AxiosResultStatus> {
    const { paymentId } = paymentDetails;
    delete paymentDetails.paymentId;
    const payload: AxiosSavePayload = {
      dataToSave: paymentDetails,
      dataId: paramId,
      isPatch: true,
      url: `payment/v2/payment/${paymentId}`,
    };
    let result: any = await axiosSaveHelper(payload);
    return result;
  }
	
	async getConfig(configId: string, configIdSource: string, configKey: string, authToken: string) {
    const serviceurl =  `/configuration/v2/configuration/customconfig/${configId}/${configIdSource}/${configKey}`;
    return await this.postApi("GET", serviceurl, null, authToken);
	}

  async getAnonymousRequestToken() {
    const { SignJWT } = await import('jose')
    const secretString = process.env.REACT_APP_AWS_GUEST_TOKEN_SECRET as string;
    const audience = process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID as string;
    const subject = process.env.REACT_APP_AWS_USER_POOL_ID as string;

    const secret = new TextEncoder().encode(secretString)

    const token = await new SignJWT({})
        .setProtectedHeader({ alg: 'HS256', typ: 'JWT' })
        .setIssuedAt()
        .setAudience(audience)
        .setSubject(subject)
        .sign(secret);
    const serviceurl =  `/user/v2/guest`;
    return await this.postApi("POST", serviceurl, null, token);
	}

  async getLaunchPayload(payload: string, authToken: string) {
    const serviceurl = `/clients/v2/client/paynow/url-params`;
    return await this.postApi("POST", serviceurl, payload, authToken, 'text/plain');
  }

  async submitPayment(payment: PayNowMakePaymentRequest | undefined, authToken: string) {
    const serviceurl =  `/payment/v2/payment`;
    return await this.postApi("POST", serviceurl, payment, authToken);
  }
	
	postApi(method:string, path:string, postBody?:any, authToken?:string, contentType?: string, cacheMode?: 'default' | 'reload' | 'no-cache') {
    let requestConfig:any = this.getRequestConfig(method, authToken, contentType);
    if (postBody) {
      requestConfig.body = JSON.stringify(postBody)
    }
    if(cacheMode) {
      requestConfig = {
        ...requestConfig,
        cache: cacheMode
      }
    }
		
		return fetch(`${process.env.REACT_APP_UNIFIED_AWS_APPGATEWAY_URI}${path}`, requestConfig)
        .then(async (response) => {
          const responseContentType = response.headers.get('Content-Type');
          if (contentType === 'text/plain') {
            return response.text();
          } else if (responseContentType?.includes('application/pdf')) {
            return await response.arrayBuffer();
          }else {
            return response.json();
          }
        })
        .then((responseData) => {
          return responseData;
        })
        .catch((error) => console.error(error));
	}


  getRequestConfig(method: string, authToken?: string, contentType?: string):any  {

    if (!contentType) {
      contentType = 'application/json';
    } 

    if ((authToken !== null) && (authToken !== undefined) && (authToken !== '')) {
      return {      
        method: method,
        headers: {
          'Accept': contentType,
          'Content-Type': contentType,
          'Authorization': 'Bearer ' + authToken,
          'x-cog-id': authToken
        },
      }
    } else {
      return {
        method: method,
        headers: {
          'Accept': contentType,
          'Content-Type': contentType
        },
      }
    }
  }

  async getClientFacility(facilityId: string, authToken: string){

    const serviceUrl = `/clients/v2/client/facility/${facilityId}/guest`
    return await this.postApi("GET", serviceUrl, null, authToken);

  }

  async getClientFacilities(clientId: number, authToken: string){
    const serviceUrl = `/clients/v2/client/${clientId}/facility`
    return await this.postApi("GET", serviceUrl, null, authToken);
  }

  async getPatientEncounter(patientEncounterId: number, authToken: string, config?: Record<string, any>) {
    const serviceurl = `/patient-encounter/v2/patient/encounter/${patientEncounterId}`;
    return await this.postApi("GET", serviceurl, null, authToken, undefined, config?.cacheMode);
  }

  async getPatient(patientId: number, authToken: string) {
    const serviceurl = `/patient/v2/patient/${patientId}`;
    return await this.postApi("GET", serviceurl, null, authToken);
  }

  async getEvaluateRule(instanceOfCare: PatientEncounter, authToken: string): Promise<AxiosResultStatus> {
    const serviceurl =
      `/patientrecord/patient/${instanceOfCare.patientId}/encounter/${instanceOfCare.patientEncounterId}/evaluaterule`;
    return await this.postApi("POST", serviceurl, instanceOfCare, authToken);
  }

  mapPaymentInfoToRequestObj(payment: Payment, patientInfo: PatientInfo, isOptimal?: boolean, extraData?: any, isForUpdate?: boolean){
    const result = {
      areDocsSigned: payment?.paymentChannelId === 4 ? true : false,
      downPmtAmt: +payment.paymentAmt,
      isACH: false,
      isHighRisk: false,
      isPaidInFull: false,
      patientPaymentProgramId: 0,
      patientPaymentSchedule: {
        downPmtAmt: Number(payment.paymentAmt.toFixed(2)),
        paymentDueAmt: isOptimal ? Number(extraData?.paymentDueAmt.toFixed(2)) : 0,
        paymentFreq: isOptimal ? "M" : "F",
        pfrAmt: patientInfo.pfrAmt,
        remainingTerms: isOptimal ? extraData?.remainingTerms : 0,
        scheduleStartDt: isOptimal ? extraData?.scheduleStartDt : moment().toISOString(),
        scheduleStatus: isOptimal ? "Active" : "Pending",
        terms: isOptimal ? extraData?.remainingTerms : 0,
      } as PatientPaymentSchedule
    } as PatientPaymentProgram;

    // Only add pfrBalance for POST (not for PUT/update)
    if (!isForUpdate) {
      result.patientPaymentSchedule.pfrBalance = patientInfo.pfrAmt;
    }
    
    return result;
  }

  async submitMobilePayment(payment: Payment, patientInfo: PatientInfo, isOptimal?: Boolean, extraData?: any, authToken?: string, selectedEncounter?: PatientEncounter, selectedPatient?: PatientViewModel, dispatch?: any, getState?: any) {

    const paymentProgramObj = this.mapPaymentInfoToRequestObj(payment, patientInfo, !!isOptimal, extraData, false);
    
    let existingProgram = null;
    
    if (selectedEncounter?.patientPaymentProgram && selectedEncounter?.patientPaymentProgram?.length > 0) {
      existingProgram = selectedEncounter?.patientPaymentProgram[0];
    } else {
      existingProgram = null;
    }
    
    // If we have an existing program, create an update-friendly version of the payment program object
    const updateProgramObj = existingProgram ? 
      this.mapPaymentInfoToRequestObj(payment, patientInfo, !!isOptimal, extraData, true) : 
      null;

    // Execute necessary pre-payment API calls
    const resolvedPromises = await Promise.all([
      patientService.updatePatient({
            contact: {
              email: payment.receiptEmail,
            }
          },
          payment.patientId!,
      ),
      patientService.saveIOCWithPartialObject({
        hasChampions: false
      }, payment.patientEncounterId!),
      // Use the appropriate method based on whether a program exists
      existingProgram && existingProgram.patientPaymentProgramId ?
        this.updatePaymentProgram(
          existingProgram.patientPaymentProgramId, 
          {
            ...updateProgramObj,
            patientPaymentProgramId: existingProgram.patientPaymentProgramId,
            patientId: payment.patientId,
            patientEncounterId: payment.patientEncounterId
          }
        ) : 
        patientService.savePatientPaymentProgram({
          paymentProgram: paymentProgramObj,
          patientId: payment.patientId!,
          encounterId: payment.patientEncounterId!
        })
    ])

    resolvedPromises.forEach((resolvedPromise: Awaited<AxiosResultStatus>) => {
      if(resolvedPromise.hasErrors){
          return resolvedPromise
      }
    });
    
    // Extract the payment program from the Promise.all results
    const [, , paymentProgram] = resolvedPromises;

    const paymentResponse = await patientService.createPayment({
      ...payment,
      isRecurringDownPayment: !!isOptimal,
      patientPaymentScheduleId: paymentProgram.entity.patientPaymentSchedule.patientPaymentScheduleId,
      paymentGroupId: paymentProgram.entity.patientPaymentSchedule.patientPaymentScheduleId.toString(),
    })

    if(paymentResponse.hasErrors) return paymentResponse;

    const convertResponse = await this.convertUsingEnroll(
      payment.patientId!, 
      payment.patientEncounterId!, 
      payment, 
      paymentProgram, 
      authToken || '',
      selectedEncounter!,
      selectedPatient!,
      dispatch,
      getState
    );

    if(convertResponse.hasErrors) return convertResponse;

    if (isOptimal) {
      const optimalUpdateProgramObj = this.mapPaymentInfoToRequestObj(payment, patientInfo, !!isOptimal, extraData, true);
      
      Promise.all([
        paymentProgram && paymentProgram.entity && paymentProgram.entity.patientPaymentProgramId ? 
        this.updatePaymentProgram(
          paymentProgram.entity.patientPaymentProgramId,
          {
            ...optimalUpdateProgramObj,
            patientPaymentProgramId: paymentProgram.entity.patientPaymentProgramId,
            isDownPmtCaptured: !!isOptimal,
            patientPaymentSchedule: {
              ...optimalUpdateProgramObj.patientPaymentSchedule,
              nextPaymentDueDt: isOptimal? moment().add(1, 'months').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
              paymentStartDay: isOptimal ? Math.min(moment().date(), 28).toString() : '',
            },
            patientId: payment.patientId!,
            patientEncounterId: payment.patientEncounterId!,
          }
        ) :
        patientService.savePatientPaymentProgram({
          paymentProgram: {
            ...optimalUpdateProgramObj,
            isDownPmtCaptured: !!isOptimal,
            patientPaymentSchedule: {
              ...optimalUpdateProgramObj.patientPaymentSchedule,
              nextPaymentDueDt: isOptimal? moment().add(1, 'months').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
              paymentStartDay: isOptimal ? Math.min(moment().date(), 28).toString() : '',
            },
            patientPaymentProgramId: paymentProgram.entity.patientPaymentProgramId,
          },
          patientId: payment.patientId!,
          encounterId: payment.patientEncounterId!
        }),
        patientService.saveIOCWithPartialObject({
          finClearanceStatus: 'Converted',
          finClearanceStatusDt: moment().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        }, payment.patientEncounterId!),
        patientService.updateDocumentSignature({
          data: {
            documentStatus: 'Available',
            signatureStatus: 'Signature Completed',
            documentLocation: 'paymentAcknowledgement'
          },
          encounterId: payment.patientEncounterId!
        }),
        patientService.saveStatus({
          // @ts-ignore
          workFlow: {
            workflowId: 3,
            workflowStatus: {
              workflowStatusId: 12,
              workflowStatusDesc: '',
            },
            workflowSubStatus: {
              workflowSubStatusId: 23,
              workflowSubStatusDesc: '',
            },
          },
          encounterId: payment.patientEncounterId!,
          patientId: payment.patientId!,
        })
      ]);
    } else {
      // For full payment (frequency F), set isPaidInFull to true after successful payment
      if (paymentProgram?.entity?.patientPaymentProgramId) {
        this.updatePaymentProgram(
          paymentProgram.entity.patientPaymentProgramId,
          {
            isPaidInFull: true,
            patientPaymentProgramId: paymentProgram.entity.patientPaymentProgramId,
            patientId: payment.patientId!,
            patientEncounterId: payment.patientEncounterId!,
          }
        );
      }
    }

    patientService.integrationUpdate({
      patientEncounterId: payment.patientEncounterId!,
      crmTypeSlug: 'salesforce',
      patientId: payment.patientId!
    });

    //need this property for conditional on the thunk level
    convertResponse.entity.paymentStatus = "Success";

    return convertResponse

  }

  async makePayment(paramId: number, paymentDetails: Payment): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: paymentDetails,
      dataId: paramId,
      url: `payment/v2/payment`,
    };
    let result: any = await axiosSaveHelper(payload);
    return result;
  }

  async eventPayment(payment: any, authToken: string) {
    const serviceurl =  `/salesforce/event/payment`;
    return await this.postApi("POST", serviceurl, payment, authToken);
  }

  async updatePaymentProgram(programId: number, data: any = {}): Promise<AxiosResultStatus> {
    
    const payload: AxiosSavePayload = {
      dataToSave: {
        ...data,
        patientPaymentProgramId: programId  // Ensure patientPaymentProgramId is included as a positive number
      },
      dataId: programId,  // Use a positive programId instead of -2
      isPatch: false,     // Change to false to use PUT instead of PATCH
      url: `payment/v2/program`,
    };
    return await axiosSaveHelper(payload);
  }

  async createRefund(paymentId: number, patientEncounterId:number,patientPaymentScheduleId:number, paymentAmt:number): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {patientEncounterId, patientPaymentScheduleId, paymentAmt},
      dataId: 0,
      isPatch: false,
      url: `payment/v2/payment/${paymentId}/refund`,
    };
    return await axiosSaveHelper(payload);
  }


  async captureDownPayment(paymentId: number): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {},
      dataId: paymentId,
      isPatch: false,
      url: `payment/v2/payment/capture-payment`,
    };
    return await axiosSaveHelper(payload);
  }

  async setNextPaymentDueDt(scheduleId: number, paymentDueDt: string): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {nextPaymentDueDt: paymentDueDt},
      dataId: -2,
      isPatch: true,
      url: `payment/v2/schedule/${scheduleId}`,
    };
    return await axiosSaveHelper(payload);
  }

  async setScheduleStartDt(scheduleId: number, scheduleStartDt: string): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {scheduleStartDt: scheduleStartDt},
      dataId: -2,
      isPatch: true,
      url: `payment/v2/schedule/${scheduleId}`,
    };
    return await axiosSaveHelper(payload);
  }

  //todo: port the following to axios-read-helper and axios-save-helper

  async getUserRecord(email: string, authToken: string): Promise<AxiosResultStatus> {
    const encodedEmail = encodeURIComponent(email);
    const serviceurl = `/user/v2/user?userName=${encodedEmail}`;
    return await this.postApi("GET", serviceurl, null, authToken);
  }

  async getPaymentProgram(patientId: number, encounterId: number, authToken?: string): Promise<AxiosResultStatus> {
    const serviceurl = `/payment/v2/program?patientId=${patientId}&encounterId=${encounterId}`;
    return await this.postApi("GET", serviceurl, null, authToken || '');
  }

  async createGuestUser(user: User, authToken: string): Promise<AxiosResultStatus> {
    const serviceurl = `/user/v2/user`;
    return await this.postApi("POST", serviceurl, user, authToken);
  }

  //to do: future work
  // async updateGuestUser(user: User, authToken: string): Promise<AxiosResultStatus> {
  //   const serviceurl = `/user/v2/user`;
  //   const payload: AxiosSavePayload = {
  //     dataToSave: user,
  //     url: serviceurl,
  //   };
  //   return await this.postApi("PUT", serviceurl, payload, authToken);
  // }

  async convertUsingEnroll(patientId: number, encounterId: number, payment: Payment, 
                          paymentProgram: PatientPaymentProgram | AxiosResultStatus, authToken: string, selectedEncounter: PatientEncounter, selectedPatient: PatientViewModel, dispatch: any, getState?: any): Promise<AxiosResultStatus> {
    try {
      
      await LedgerUtils.fetchLedger({
        encounterId: selectedEncounter.patientEncounterId,
        dispatch: dispatch,
      });

      const state = getState() as RootState;
      const ledgerData = state.patientContext?.selectedEncounter?.encounterLedger;
      
      // Use centralized downpayments calculation
      const downPayments = ledgerData?.downpayments || [];
      const computedDownPaymentsSum = ledgerData?.downpaymentsSum || 0;

      const actualPaymentProgram = 'entity' in paymentProgram ? paymentProgram.entity : paymentProgram;
      const isZeroDown = actualPaymentProgram.downPmtAmt === 0;
      const isZeroDownZeroRecurring = isZeroDown && 
        actualPaymentProgram.patientPaymentSchedule.terms === 0 && 
        actualPaymentProgram.patientPaymentSchedule.paymentDueAmt === 0 &&
        actualPaymentProgram.patientPaymentSchedule.paymentFreq !== 'F';

      // Call Utils.enroll with the required parameters
      await Utils.enroll({
        selectedEncounter: selectedEncounter,
        paymentProgram: actualPaymentProgram,
        downPayments: downPayments,
        isCovertDisabled: false,
        isZeroDownZeroRecurring,
        setIsMissingReqModalOpen: () => {},
        setIsEnrollLoading: () => {},
        dispatch: dispatch,
        userName: selectedPatient?.contact?.email, // guest payment user as convert user id
        computedDownPaymentsSum: computedDownPaymentsSum,
        client: selectedEncounter.client,
        isIntegrationEnabled: false, //false for guest payment for now
        isEnrollFlow: null,
        selectedPatient: selectedPatient,
        isZeroDown,
        processError: (response) => {
          console.error("Error in payment processing:", response);
          return;
        },
        processUserRecord: async (email, isUpdateOnly, callback, options) => {
          try {
            // First try to get the user record
            const userResponse = await this.getUserRecord(email, authToken);
            
            if (!userResponse.hasErrors && userResponse.entity && userResponse.entity.length > 0) {
              return userResponse.entity[0];
            }
            
            // If not found, create a new user
            const userClientScopeToSave = {
              clientId: selectedEncounter.clientId,
              isFacilityLevel: true,
              allowedFacilities: []
            };
            
            // Create a simplified user object with only the necessary fields
            // The API will handle default values for missing fields
            const userToCreate = {
              userName: email,
              firstName: options.firstName || selectedPatient?.contact?.firstName || "",
              lastName: options.lastName || selectedPatient?.contact?.lastName || "",
              userScope: "FINPAY",
              clientId: selectedEncounter.clientId,
              isActive: true,
              isPatient: true,
              convRunNum: 1,
              userRoleId: 4, // Account Holder role
              email: email,
              mobilePhone: selectedPatient?.contact?.primPhoneNum || ""
            };
            
            const newUserResponse = await this.createGuestUser(userToCreate as any, authToken);
            
            if (newUserResponse.hasErrors) {
              throw new Error(`Failed to create user: ${newUserResponse.errorMessage}`);
            }
            
            return newUserResponse.entity;
          } catch (error) {
            console.error("Error processing user record:", error);
            throw error;
          }
        },
        mapToDownPayment: () => {
          const currentDateTime = new Date();
          
          return {
            paymentId: 0,
            isManual: true, 
            isAuthExpired: false,
            paymentGroupId: `${actualPaymentProgram.patientPaymentSchedule.patientPaymentScheduleId}`,
            patientPaymentScheduleId: actualPaymentProgram.patientPaymentSchedule.patientPaymentScheduleId,
            patientId: selectedEncounter.patientId,
            patientEncounterId: selectedEncounter.patientEncounterId,
            paymentAmt: 0,
            paymentType: { paymentTypeId: 1, paymentTypeName: "Down Payment" },
            paymentInitDt: currentDateTime.toISOString(),
            isAuthOnly: true,
            receiptEmail: selectedPatient?.contact?.email || "guest@finpay.com",
            paymentChannelId: payment.paymentChannelId || 1 // Use payment's channel or default to 1
          };
        }
      });
      
      // Return a success response similar to what convertPatientEncounter would return
      return {
        hasErrors: false,
        entity: {
          paymentStatus: "Success",
        },
        errorMessage: ""
      };
    } catch (error) {
      console.error("Error in convertUsingEnroll:", error);
      return {
        hasErrors: true,
        entity: null,
        errorMessage: error instanceof Error ? error.message : "Unknown error during conversion"
      };
    }
  }
}

export const paynowService = new PayNowService();